'use client';
// Mirrors the layout and chrome of app/(status)/ pages. Next.js requires
// global-error.tsx at the app root; route groups don't apply to it.

import '../styles/globals.css';

import { logger } from '@magicschool/logger';
import { Button, Link, Text, ThemeProvider } from '@magicschool/spellbook';
import { DEFAULT_LOCALE, type Language } from '@magicschool/supabase/types';
import { GoogleTagManager } from '@next/third-parties/google';
import NextLink from 'next/link';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { StatusContainer } from '@/components/_containers/StatusContainer';
import AmplitudeInitializer from '@/components/amplitude/AmplitudeInitializerClient';
import { LocalesProvider } from '@/components/ui-component/LocalesProviderClient';
import { isLanguageRightToLeft } from '@/features/languages/utils';
import { EmptyLayout as StatusLayout } from '@/layouts/EmptyLayout';
import { trackRUMError } from '@/lib/observability/rum';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';
import { getCookieValue } from '@/util/cookies';

type TrackError = {
  errorMessage: string;
  locale: string | null;
  digest?: string;
  requestUri?: string;
  search?: string;
  hash?: string;
};

type DigestError = Error & { digest?: string };

type GlobalErrorProps = {
  error: DigestError;
};

export default function GlobalError({ error }: GlobalErrorProps) {
  const localeCookie = getCookieValue('locale');
  const locale = (localeCookie || DEFAULT_LOCALE) as Language;
  const rtl = isLanguageRightToLeft(locale);

  const resetPage = () => {
    location.reload();
  };

  useEffect(() => {
    const err: TrackError = {
      errorMessage: error.message,
      digest: error.digest,
      locale: localeCookie,
    };
    if (typeof window !== 'undefined') {
      err.requestUri = window.location.href;
      err.search = window.location.search;
      err.hash = window.location.hash;
      try {
        analyticsTrack('GlobalError:Error', err);
      } catch (e) {
        logger.error('Error tracking error analytics', e);
      }
    }
    trackRUMError(error, err);
  }, [error, localeCookie]);

  return (
    <html lang={locale} dir={rtl ? 'rtl' : 'ltr'} suppressHydrationWarning>
      <body>
        <LocalesProvider locale={locale}>
          <ThemeProvider dir={rtl ? 'rtl' : 'ltr'} enableSystem>
            <StatusLayout>
              <StatusContainer>
                <StatusContainer.Illustration src="/icons/icon-traffic-cone.svg" width={252} height={174} priority />
                <StatusContainer.Heading>
                  <FormattedMessage id="global-error.heading" />
                </StatusContainer.Heading>
                <StatusContainer.Body>
                  <div className="space-y-4">
                    <Text>
                      <FormattedMessage id="global-error.message" />
                    </Text>
                    <Text>
                      <FormattedMessage
                        id="global-error.cookies-message"
                        values={{
                          Link: (chunks) => (
                            <Link
                              key="link"
                              href={process.env.NEXT_PUBLIC_CLEAR_COOKIES_URL || ''}
                              target="_blank"
                              showExternalIcon={false}
                            >
                              {chunks}
                            </Link>
                          ),
                        }}
                      />
                    </Text>
                  </div>
                </StatusContainer.Body>
                <StatusContainer.Actions>
                  <Button variant="outlined" color="neutral" onClick={resetPage}>
                    <FormattedMessage id="try-again" />
                  </Button>
                  <Button asChild>
                    <NextLink href="/">
                      <FormattedMessage id="return-home" />
                    </NextLink>
                  </Button>
                </StatusContainer.Actions>
              </StatusContainer>
            </StatusLayout>
          </ThemeProvider>
        </LocalesProvider>
        <AmplitudeInitializer />
        <GoogleTagManager
          preview={process.env.NEXT_PUBLIC_GTM_PREVIEW}
          auth={process.env.NEXT_PUBLIC_GTM_AUTH}
          gtmId={process.env.NEXT_PUBLIC_GTM_TAG_ID || ''}
        />
      </body>
    </html>
  );
}
